import React from 'react'
import { FiArrowUpRight, FiGithub, FiArrowRight } from 'react-icons/fi'

export default function ProjectFeature({ project, index }) {
  const number = String(index + 1).padStart(2, '0')

  return (
    <article id={`project-${project.id}`} aria-labelledby={`project-${project.id}-title`} className="project-feature scroll-mt-24 border border-edge rounded-2xl overflow-hidden">
      <div className="grid lg:grid-cols-[1fr_1.1fr]">
        <div className="p-6 sm:p-9 lg:p-10 min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-8 font-mono text-[10px] sm:text-xs uppercase tracking-[0.12em]">
            <span className="text-lime">{index === 0 ? 'Featured' : 'Project'} / {number}</span>
            <span className="text-cream-dim">{project.category}</span>
          </div>
          <h3 id={`project-${project.id}-title`} className="font-display font-bold text-3xl sm:text-4xl tracking-tight leading-[1.1]">
            {project.headline}<br />
            <span className="text-lime">{project.accent}</span>
          </h3>
          <p className="font-mono text-xs text-cream-dim uppercase tracking-wide mt-4 mb-6">{project.name}</p>
          <p className="text-cream-dim leading-relaxed text-sm sm:text-base">{project.description}</p>
          <ul aria-label={`${project.name} technologies`} className="flex flex-wrap gap-2 mt-6">
            {project.tech.map(tech => (
              <li key={tech} className="font-mono text-[10px] sm:text-xs text-cream-dim border border-edge px-2.5 py-1 rounded-full">{tech}</li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center gap-5 mt-8">
            {project.live && (
              <a href={project.live} target="_blank" rel="noopener noreferrer" className="portfolio-button inline-flex items-center gap-3 rounded-full bg-lime px-5 py-3 text-dark text-sm font-medium">
                {project.action} <FiArrowUpRight aria-hidden="true" />
              </a>
            )}
            <a href={project.github} target="_blank" rel="noopener noreferrer" aria-label={`View ${project.name} source code on GitHub`} className={project.live
              ? 'portfolio-text-link inline-flex items-center gap-2 text-sm text-cream'
              : 'portfolio-button inline-flex items-center gap-3 rounded-full bg-lime px-5 py-3 text-dark text-sm font-medium'}>
              <FiGithub aria-hidden="true" /> {project.live ? 'Source code' : 'Explore the source'}
            </a>
          </div>
          {project.availability && <p className="mt-4 text-xs text-cream-dim">{project.availability}</p>}
        </div>
        <figure className="flex flex-col justify-center min-w-0 px-6 pb-6 sm:px-9 sm:pb-9 lg:pt-9 lg:pl-0">
          <div className="border border-edge rounded-xl bg-dark overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-edge px-4 py-3 font-mono text-[10px] text-cream-dim">
              <span className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-lime" aria-hidden="true" />{project.previewLabel}</span>
              <span>{project.previewMeta}</span>
            </div>
            <div className={project.imageLayout === 'phones' ? 'grid grid-cols-2 gap-3 p-4 sm:p-6 bg-dark-elevated/30' : ''}>
              {project.images.map(image => (
                <img key={image.src} src={image.src} alt={image.alt} width={image.width} height={image.height} loading="lazy" className={project.imageLayout === 'phones' ? 'w-full h-auto max-h-96 object-contain' : 'w-full h-auto'} />
              ))}
            </div>
          </div>
          <figcaption className="flex flex-wrap items-center justify-between gap-3 mt-4 text-xs text-cream-dim">
            <span>{project.caption}</span>
            {project.related && <a href={project.related.href} className="portfolio-text-link inline-flex items-center gap-1 shrink-0 text-lime">{project.related.label} <FiArrowRight aria-hidden="true" /></a>}
          </figcaption>
        </figure>
      </div>
      <ol className="grid sm:grid-cols-3 border-t border-edge bg-dark/50">
        {project.steps.map(([title, body], stepIndex) => (
          <li key={title} className="project-step px-6 py-6 sm:px-8 min-w-0">
            <span className="font-mono text-xs text-lime" aria-hidden="true">{String(stepIndex + 1).padStart(2, '0')}</span>
            <h4 className="font-display font-semibold text-base text-cream mt-3 mb-1">{title}</h4>
            <p className="text-sm leading-relaxed text-cream-dim">{body}</p>
          </li>
        ))}
      </ol>
      <div className="px-6 sm:px-9 py-5 border-t border-edge flex flex-col sm:flex-row gap-2 sm:gap-6 text-xs leading-relaxed">
        <span className="font-mono uppercase text-lime shrink-0">{project.detailLabel}</span>
        <p className="text-cream-dim">{project.detail}</p>
      </div>
    </article>
  )
}
